import { SUCCESS_RESPONSE_TYPE } from 'api/constants'
import { useQuery, useQueryClient } from 'react-query'
import { useStore } from 'react-redux'
import { InformalFeedbackRequest, QueryOptions } from 'api/clients/InformalFeedbackRequestsClient'
import produce from 'immer'
import { InformalFeedbackRequestsService } from 'api/services/AlmanacFiles/InformalFeedbackRequestsService'
import { informalFeedbackRequestsKeys } from '../informalFeedbackRequestsKeys'

interface Props extends QueryOptions {
  almanacFileId: string
  staleTime?: number
  enabled?: boolean
}

type CacheData = { informal_feedback_requests: InformalFeedbackRequest[] } | undefined

export const useInformalFeedbackRequests = ({ almanacFileId, enabled = true, ...rest }: Props) => {
  const {
    staleTime = 1000 * 60 * 5, // time in milliseconds
    ...options
  } = rest
  const queryClient = useQueryClient()
  const store = useStore()
  return useQuery(
    informalFeedbackRequestsKeys.forAlmanacFile(almanacFileId),
    async () => {
      if (!enabled) return null

      const res = await InformalFeedbackRequestsService.index(store, almanacFileId, options)
      if (res.type !== SUCCESS_RESPONSE_TYPE) {
        throw new Error()
      }

      res.data.informal_feedback_requests.forEach((request) => {
        queryClient.setQueriesData<{ informal_feedback_request: InformalFeedbackRequest } | undefined>(
          [informalFeedbackRequestsKeys.prefix, 'detail', request.id],
          (old) => {
            return produce(old, (draft) => {
              if (!draft) {
                return
              }

              draft.informal_feedback_request = request
            })
          },
        )
      })

      return res.data
    },
    {
      initialData: () => {
        const existingArticleRequests = queryClient.getQueryData<CacheData>(
          [informalFeedbackRequestsKeys.prefix, almanacFileId],
          { exact: false },
        )?.informal_feedback_requests

        const matchedRequests = existingArticleRequests?.filter((request) => {
          return options.states ? options.states.includes(request.state) : true
        })

        if (!matchedRequests || matchedRequests.length < 1) return

        return {
          informal_feedback_requests: matchedRequests,
        }
      },
      refetchOnWindowFocus: false,
      staleTime,
    },
  )
}
